import { Children, ReactNode, isValidElement, useMemo } from "react";
import { Avatar, Typography, Paper, Box, useMediaQuery, useTheme } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import { normalizeMarkdown } from "../utils/normalizeMarkdown";

interface Props {
  message: {
    id: number;
    text: string;
    from: "user" | "bot";
    created_at?: string;
  };
}

const formatTimestamp = (timestamp?: string) => {
  if (!timestamp) return "Agora";

  const messageDate = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - messageDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Agora";
  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays < 7) return `há ${diffDays}d`;

  return messageDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });
};

function extractText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return extractText(props.children);
  }
  return "";
}

function getChildren(element: React.ReactElement): ReactNode {
  return (element.props as { children?: ReactNode }).children;
}

function extractTableData(children: ReactNode) {
  const headers: string[] = [];
  const rows: string[][] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const tag = typeof child.type === "string" ? child.type : "";

    if (tag === "thead") {
      Children.forEach(getChildren(child), (tr: ReactNode) => {
        if (!isValidElement(tr)) return;
        Children.forEach(getChildren(tr), (th: ReactNode) => {
          if (!isValidElement(th)) return;
          headers.push(extractText(getChildren(th)));
        });
      });
    } else if (tag === "tbody") {
      Children.forEach(getChildren(child), (tr: ReactNode) => {
        if (!isValidElement(tr)) return;
        const row: string[] = [];
        Children.forEach(getChildren(tr), (td: ReactNode) => {
          if (!isValidElement(td)) return;
          row.push(extractText(getChildren(td)));
        });
        rows.push(row);
      });
    }
  });

  return { headers, rows };
}

function ResponsiveTable({ children }: { children: ReactNode }) {
  const theme = useTheme();
  // Cards até < lg (1280px): cobre iPad portrait E landscape (1024px) onde
  // a <table> com 5+ colunas ficava espremida (palavras quebrando entre
  // letras). Só desktop e iPad Pro 12.9" landscape recebem <table>.
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));

  if (!isCompact) {
    return (
      <Box
        sx={{
          overflowX: "auto",
          mb: 2,
          "& table": {
            // width:auto + minWidth:100% + whiteSpace:nowrap nas cells
            // permite a tabela expandir além do container e o overflowX
            // virar scroll real (com width:100% a tabela se comprimia e
            // quebrava palavras letra-por-letra).
            width: "auto",
            minWidth: "100%",
            borderCollapse: "collapse",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            background: "rgba(26, 49, 60, 0.2)",
          },
          "& thead": {
            background: "linear-gradient(to right, #E2725B, #CC3B2A)",
          },
          "& tbody tr": {
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          },
          "& tr:hover": {
            backgroundColor: "rgba(255,255,255,0.05)",
          },
          "& th": {
            padding: "20px 24px",
            textAlign: "left",
            fontWeight: 700,
            color: "#ffffff",
            fontSize: "17px",
            whiteSpace: "nowrap",
            borderRight: "1px solid rgba(255,255,255,0.2)",
            "&:last-child": { borderRight: "none" },
          },
          "& td": {
            padding: "20px 24px",
            color: "#ffffff",
            fontSize: "17px",
            lineHeight: 1.7,
            fontWeight: 400,
            whiteSpace: "nowrap",
            borderRight: "1px solid rgba(255,255,255,0.1)",
            "&:last-child": { borderRight: "none" },
          },
        }}
      >
        <table>{children}</table>
      </Box>
    );
  }

  const { headers, rows } = extractTableData(children);

  return (
    <div className="space-y-3 mb-4">
      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className="rounded-xl bg-petrolio-dark/30 border border-white/10 overflow-hidden"
        >
          {row.map((cell, cellIdx) => (
            <div
              key={cellIdx}
              className="px-4 py-3 border-b border-white/5 last:border-b-0"
            >
              {headers[cellIdx] && (
                <span className="block text-saluton font-bold text-[13px] sm:text-[14px] mb-1">
                  {headers[cellIdx]}
                </span>
              )}
              <span className="block text-white text-[15px] sm:text-[16px] leading-relaxed">
                {cell}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function ChatMessage({ message }: Props) {
  const isUser = message.from === "user";
  // Defesa em profundidade: ChatPage já normaliza durante streaming, mas
  // mensagens carregadas do histórico passam pelo render direto sem
  // normalização. Aplicar aqui idempotentemente cobre todos os caminhos.
  const renderedText = useMemo(
    () => (isUser ? message.text : normalizeMarkdown(message.text)),
    [isUser, message.text],
  );

  return (
    <div className={`flex gap-2 md:gap-4 min-w-0 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {isUser ? (
        <Avatar
          className="bg-gradient-to-br from-petrolio to-petrolio-dark shadow-lg shrink-0"
          sx={{ width: { xs: 32, md: 48 }, height: { xs: 32, md: 48 } }}
        >
          <PersonIcon sx={{ color: "#ffffff", fontSize: { xs: "20px", md: "28px" } }} />
        </Avatar>
      ) : (
        <div className="relative shrink-0">
          <div className="absolute inset-0 bg-saluton/30 rounded-full blur-lg"></div>
          <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
            <DrWeedyLogo width={40} height={40} />
          </div>
        </div>
      )}

      <Paper
        elevation={3}
        sx={{
          maxWidth: { xs: "100%", sm: "92%", md: "900px" },
          minWidth: 0,
          padding: { xs: "12px 14px", sm: "20px 24px", md: "24px 32px" },
          background: isUser
            ? "linear-gradient(135deg, rgba(64, 120, 139, 0.6) 0%, rgba(26, 49, 60, 0.7) 100%)"
            : "linear-gradient(135deg, rgba(26, 49, 60, 0.9) 0%, rgba(4, 9, 10, 0.95) 100%)",
          borderRadius: isUser ? "24px 24px 8px 24px" : "24px 24px 24px 8px",
          border: isUser
            ? "1px solid rgba(64, 120, 139, 0.4)"
            : "1px solid rgba(226, 114, 91, 0.3)",
          backdropFilter: "blur(20px)",
          boxShadow: isUser
            ? "0 8px 32px rgba(0,0,0,0.3)"
            : "0 12px 48px rgba(0,0,0,0.5)",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {isUser ? (
          <>
            <Typography
              variant="body1"
              data-test="message-bubble"
              sx={{
                color: "#ffffff",
                lineHeight: 1.8,
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
              }}
            >
              {message.text}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                marginTop: "8px",
                display: "block",
                fontWeight: 500,
                fontSize: "13px",
                textAlign: "right",
              }}
            >
              {formatTimestamp(message.created_at)}
            </Typography>
          </>
        ) : (
          <div className="markdown-content leading-relaxed overflow-hidden">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "20px",
                      color: "#ffffff",
                      fontSize: { xs: "20px", sm: "24px", md: "28px" },
                      fontFamily: "Oxanium, sans-serif",
                    }}
                  >
                    {children}
                  </Typography>
                ),
                h2: ({ children }) => (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "16px",
                      color: "#ffffff",
                      fontSize: { xs: "17px", sm: "20px", md: "24px" },
                      fontFamily: "Oxanium, sans-serif",
                    }}
                  >
                    {children}
                  </Typography>
                ),
                h3: ({ children }) => (
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      marginBottom: "12px",
                      color: "#ffffff",
                      fontSize: { xs: "16px", sm: "18px", md: "20px" },
                      fontFamily: "Oxanium, sans-serif",
                    }}
                  >
                    {children}
                  </Typography>
                ),
                p: ({ children }) => (
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: { xs: "12px", sm: "16px" },
                      lineHeight: 1.8,
                      color: "#ffffff",
                      fontSize: { xs: "14px", sm: "16px", md: "18px" },
                      fontWeight: 400,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                ul: ({ children }) => (
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: "disc",
                      pl: { xs: 2.5, sm: 3, md: 3.5 },
                      mb: { xs: 1.5, sm: 2 },
                      color: "#ffffff",
                      "& > li + li": { mt: { xs: 1, sm: 1.5 } },
                    }}
                  >
                    {children}
                  </Box>
                ),
                // `start` precisa ser repassado: quando a lista não começa em
                // 1 — porque bullets soltos entre os itens quebraram a lista
                // anterior em duas — o react-markdown informa o número
                // inicial aqui. Descartá-lo fazia toda lista recomeçar em 1,
                // e a escada de escalada saía como "1." e "1." em vez de
                // "1." e "2.".
                ol: ({ children, start }) => (
                  <Box
                    component="ol"
                    start={start}
                    sx={{
                      listStyleType: "decimal",
                      pl: { xs: 2.5, sm: 3, md: 3.5 },
                      mb: { xs: 1.5, sm: 2 },
                      color: "#ffffff",
                      "& > li + li": { mt: { xs: 1, sm: 1.5 } },
                    }}
                  >
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Box
                    component="li"
                    sx={{
                      color: "#ffffff",
                      fontSize: { xs: "14px", sm: "16px", md: "17px" },
                      lineHeight: 1.8,
                      mb: { xs: 0.5, sm: 0.75 },
                    }}
                  >
                    {children}
                  </Box>
                ),
                strong: ({ children }) => (
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "inherit",
                    }}
                  >
                    {children}
                  </span>
                ),
                em: ({ children }) => (
                  <span
                    className="italic text-white"
                    style={{ fontSize: "inherit" }}
                  >
                    {children}
                  </span>
                ),
                // Aba nova: a resposta referencia a triagem de segurança
                // (/seguranca) e navegar no mesmo lugar tiraria o médico da
                // conversa. Vale para qualquer link que apareça na resposta.
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "rgba(226, 114, 91, 0.9)",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  >
                    {children}
                  </a>
                ),
                code: ({ children, className }) => (
                  <code
                    className={`
                    ${
                      className?.includes("language-")
                        ? "block bg-petrolio-dark/30 p-3 md:p-5 rounded-xl text-sm md:text-lg overflow-x-auto border border-white/10"
                        : "bg-petrolio/20 px-2 py-1 md:px-3 md:py-1.5 rounded text-sm md:text-lg border border-white/10"
                    }
                    font-mono text-saluton-light
                  `}
                  >
                    {children}
                  </code>
                ),
                blockquote: ({ children }) => (
                  <div className="border-l-4 border-saluton pl-4 md:pl-6 py-2 md:py-3 bg-saluton/10 rounded-r-lg mb-3 md:mb-4 text-white text-base md:text-lg font-medium">
                    {children}
                  </div>
                ),
                table: ({ children }) => (
                  <ResponsiveTable>{children}</ResponsiveTable>
                ),
              }}
            >
              {renderedText}
            </ReactMarkdown>
          </div>
        )}

        {!isUser && (
          <Typography
            variant="body1"
            sx={{
              color: "rgba(226, 114, 91, 0.9)",
              marginTop: { xs: "8px", md: "12px" },
              display: "block",
              fontWeight: 700,
              fontSize: { xs: "12px", md: "15px" },
            }}
          >
            Dr. Weedy • {formatTimestamp(message.created_at)}
          </Typography>
        )}
      </Paper>
    </div>
  );
}
